/**
 * Payload builders for the Triple Whale custom events (BBNT_TripleWhaleEvents,
 * docs/tw-events-custom-events-setup.md): the property names and types are the ones
 * defined in the Triple Whale Custom Event Builder, ids are Magento entity ids as strings.
 */

/**
 * The longest `item_ids` sent, commas included. Triple Whale silently drops a whole custom
 * event when one of its string properties is longer than 100 characters (measured on
 * 30 Sep 2026: 100 stored, 101 dropped, with no error anywhere) - a listing of 50 ids never
 * arrived. Ids are ASCII digits, so characters and bytes agree.
 */
export const TRIPLEWHALE_MAX_ITEM_IDS_LENGTH = 100;

/**
 * As many whole ids, in list order, as fit into TRIPLEWHALE_MAX_ITEM_IDS_LENGTH once
 * comma-joined; the rest of the list is left out, never cut mid-id.
 */
const joinItemIdsWithinLimit = (itemIds: string[]): string => {
  let joined = "";
  for (const id of itemIds) {
    const next = joined === "" ? id : `${joined},${id}`;
    if (next.length > TRIPLEWHALE_MAX_ITEM_IDS_LENGTH) {
      break;
    }
    joined = next;
  }
  return joined;
};

export type TripleWhaleListPageType = "category" | "search" | "recommendation";

export interface TripleWhaleItemList {
  listId: string;
  listName: string;
  pageType: TripleWhaleListPageType;
  itemIds: string[];
  searchTerm?: string;
}

export const buildViewItemListProperties = (list: TripleWhaleItemList) => {
  const itemIds = list.itemIds.filter((id) => id !== "");
  return {
    category: "engagement",
    subcategory: "page_view",
    list_id: list.listId,
    list_name: list.listName,
    page_type: list.pageType,
    // the whole list is counted even when item_ids carries only its first ids
    items_count: itemIds.length,
    item_ids: joinItemIdsWithinLimit(itemIds),
    ...(list.searchTerm ? { search_term: list.searchTerm } : {}),
  };
};

export const buildSelectItemProperties = (packet: {
  itemId: string;
  variantId?: string;
  listId: string;
  listName: string;
  /** 1-based position in the list. */
  position: number;
}) => ({
  category: "engagement",
  subcategory: "button_click",
  item_id: packet.itemId,
  variant_id: packet.variantId || packet.itemId,
  list_id: packet.listId,
  list_name: packet.listName,
  position: packet.position,
});
